import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { MarkdownRenderer } from './MarkdownRenderer';
import { applyMarkdownFormatting, MarkdownFormat } from './markdownFormatting';
import { getMarkdownHistoryKeyboardOffset } from './markdownHistory';
import {
  BoldIcon,
  ChevronDownIcon,
  InlineCodeIcon,
  ItalicIcon,
  LinkIcon,
  OrderedListIcon,
  QuoteIcon,
  RedoIcon,
  StatusCheckIcon,
  TableIcon,
  UndoIcon,
  UnorderedListIcon,
} from './MarkdownToolbarIcons';
import {
  getEffectiveMarkdownMaxLength,
  getMarkdownSafetyError,
  MARKDOWN_MAX_LENGTH,
} from './markdownSafety';

const markdownStyles = require('./markdown.css');

const HISTORY_LIMIT = 100;

type EditorMode = 'write' | 'preview';

export interface MarkdownEditorProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  maxLength?: number;
  placeholder?: string;
  ariaDescribedBy?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  emptyPreviewMessage?: string;
}

interface ToolbarAction {
  format: MarkdownFormat;
  label: string;
  icon: React.ReactNode;
  showLabel?: boolean;
}

interface ToolbarActionGroup {
  label: string;
  actions: ToolbarAction[];
}

interface EditorHistory {
  values: string[];
  index: number;
}

const toolbarActionGroups: ToolbarActionGroup[] = [
  {
    label: 'Text formatting',
    actions: [
      { format: 'bold', label: 'Bold', icon: <BoldIcon /> },
      { format: 'italic', label: 'Italic', icon: <ItalicIcon /> },
    ],
  },
  {
    label: 'Lists',
    actions: [
      {
        format: 'bulleted-list',
        label: 'Bulleted list',
        icon: <UnorderedListIcon />,
      },
      {
        format: 'numbered-list',
        label: 'Numbered list',
        icon: <OrderedListIcon />,
      },
    ],
  },
  {
    label: 'Inline formatting',
    actions: [
      {
        format: 'inline-code',
        label: 'Inline code',
        icon: <InlineCodeIcon />,
      },
      { format: 'link', label: 'Link', icon: <LinkIcon /> },
    ],
  },
  {
    label: 'Blocks',
    actions: [
      {
        format: 'table',
        label: 'Table',
        icon: <TableIcon />,
        showLabel: true,
      },
      {
        format: 'quote',
        label: 'Quote',
        icon: <QuoteIcon />,
        showLabel: true,
      },
    ],
  },
];

const createHistory = (value: string): EditorHistory => ({
  values: [value],
  index: 0,
});

export const MarkdownEditor = ({
  id,
  value,
  onChange,
  disabled = false,
  maxLength,
  placeholder = 'Start writing Markdown…',
  ariaDescribedBy,
  ariaLabel,
  ariaLabelledBy,
  emptyPreviewMessage = 'Nothing to preview yet.',
}: MarkdownEditorProps) => {
  const [mode, setMode] = useState<EditorMode>('write');
  const [, setHistoryRevision] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const writeTabRef = useRef<HTMLButtonElement>(null);
  const previewTabRef = useRef<HTMLButtonElement>(null);
  const historyRef = useRef<EditorHistory>(createHistory(value));
  const pendingSelection = useRef<
    | {
        value: string;
        start: number;
        end: number;
      }
    | undefined
  >();

  const effectiveMaxLength = getEffectiveMarkdownMaxLength(maxLength);
  const safetyError = getMarkdownSafetyError(value, effectiveMaxLength);
  const writeTabId = `${id}-write-tab`;
  const previewTabId = `${id}-preview-tab`;
  const writePanelId = `${id}-write-panel`;
  const previewPanelId = `${id}-preview-panel`;
  const headingSelectId = `${id}-heading`;
  const statusId = `${id}-status`;
  const describedBy = [statusId, ariaDescribedBy].filter(Boolean).join(' ');
  const history = historyRef.current;
  const canUndo = history.index > 0;
  const canRedo = history.index < history.values.length - 1;

  useEffect(() => {
    const currentHistory = historyRef.current;
    const currentHistoryValue = currentHistory.values[currentHistory.index];
    const containsValueAboveLimit =
      currentHistory.values.length > 1 &&
      currentHistory.values.some(
        (historyValue) => historyValue.length > effectiveMaxLength
      );

    if (currentHistoryValue !== value || containsValueAboveLimit) {
      historyRef.current = createHistory(value);
      setHistoryRevision((revision) => revision + 1);
    }
  }, [effectiveMaxLength, value]);

  useEffect(() => {
    const pending = pendingSelection.current;

    if (!pending || pending.value !== value || !textareaRef.current) {
      return;
    }

    textareaRef.current.focus();
    textareaRef.current.setSelectionRange(pending.start, pending.end);
    pendingSelection.current = undefined;
  }, [value]);

  const recordHistory = (nextValue: string) => {
    const currentHistory = historyRef.current;
    if (currentHistory.values[currentHistory.index] === nextValue) {
      return;
    }

    const nextValues = currentHistory.values
      .slice(0, currentHistory.index + 1)
      .concat(nextValue)
      .slice(-HISTORY_LIMIT);
    historyRef.current = {
      values: nextValues,
      index: nextValues.length - 1,
    };
    setHistoryRevision((revision) => revision + 1);
  };

  const updateValue = (nextValue: string) => {
    recordHistory(nextValue);
    onChange(nextValue);
  };

  const moveThroughHistory = (offset: -1 | 1) => {
    const currentHistory = historyRef.current;
    const nextIndex = Math.max(
      0,
      Math.min(currentHistory.index + offset, currentHistory.values.length - 1)
    );

    if (nextIndex === currentHistory.index) {
      return;
    }

    const nextValue = currentHistory.values[nextIndex];
    if (nextValue.length > effectiveMaxLength) {
      return;
    }

    historyRef.current = { ...currentHistory, index: nextIndex };
    pendingSelection.current = {
      value: nextValue,
      start: nextValue.length,
      end: nextValue.length,
    };
    setHistoryRevision((revision) => revision + 1);
    onChange(nextValue);
  };

  const changeMode = (nextMode: EditorMode, focusTab = false) => {
    setMode(nextMode);

    if (focusTab) {
      const tabRef = nextMode === 'write' ? writeTabRef : previewTabRef;
      tabRef.current?.focus();
    }
  };

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    currentMode: EditorMode
  ) => {
    if (
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      event.key !== 'Home' &&
      event.key !== 'End'
    ) {
      return;
    }

    event.preventDefault();
    const nextMode =
      event.key === 'Home'
        ? 'write'
        : event.key === 'End'
        ? 'preview'
        : currentMode === 'write'
        ? 'preview'
        : 'write';
    changeMode(nextMode, true);
  };

  const formatSelection = (format: MarkdownFormat) => {
    const textarea = textareaRef.current;

    if (!textarea) {
      return;
    }

    const result = applyMarkdownFormatting(
      value,
      textarea.selectionStart,
      textarea.selectionEnd,
      format
    );

    if (result.value.length > effectiveMaxLength) {
      textarea.focus();
      return;
    }

    pendingSelection.current = {
      value: result.value,
      start: result.selectionStart,
      end: result.selectionEnd,
    };
    updateValue(result.value);
  };

  const handleTextareaKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    const historyOffset = getMarkdownHistoryKeyboardOffset(event);
    if (historyOffset === undefined) {
      return;
    }

    event.preventDefault();
    moveThroughHistory(historyOffset);
  };

  return (
    <div className={markdownStyles.editor}>
      <div
        className={markdownStyles.tabs}
        role="tablist"
        aria-label="Markdown editor mode"
      >
        <button
          ref={writeTabRef}
          id={writeTabId}
          type="button"
          role="tab"
          aria-selected={mode === 'write'}
          aria-controls={writePanelId}
          tabIndex={mode === 'write' ? 0 : -1}
          className={`${markdownStyles.tab} ${
            mode === 'write' ? markdownStyles.activeTab : ''
          }`}
          onClick={() => changeMode('write')}
          onKeyDown={(event) => handleTabKeyDown(event, 'write')}
        >
          Write
        </button>
        <button
          ref={previewTabRef}
          id={previewTabId}
          type="button"
          role="tab"
          aria-selected={mode === 'preview'}
          aria-controls={previewPanelId}
          tabIndex={mode === 'preview' ? 0 : -1}
          className={`${markdownStyles.tab} ${
            mode === 'preview' ? markdownStyles.activeTab : ''
          }`}
          onClick={() => changeMode('preview')}
          onKeyDown={(event) => handleTabKeyDown(event, 'preview')}
        >
          Preview
        </button>
      </div>

      <div
        id={writePanelId}
        role="tabpanel"
        aria-labelledby={writeTabId}
        hidden={mode !== 'write'}
      >
        <div
          className={markdownStyles.toolbar}
          role="group"
          aria-label="Format Markdown"
        >
          <div className={markdownStyles.headingControl}>
            <label
              className={markdownStyles.visuallyHidden}
              htmlFor={headingSelectId}
            >
              Heading level
            </label>
            <select
              id={headingSelectId}
              className={markdownStyles.headingSelect}
              value=""
              disabled={disabled}
              onChange={(event) => {
                if (event.currentTarget.value) {
                  formatSelection(event.currentTarget.value as MarkdownFormat);
                }
              }}
            >
              <option value="">Heading</option>
              <option value="heading-1">Heading 1</option>
              <option value="heading-2">Heading 2</option>
              <option value="heading-3">Heading 3</option>
              <option value="heading-4">Heading 4</option>
            </select>
            <ChevronDownIcon className={markdownStyles.headingChevron} />
          </div>

          {toolbarActionGroups.map((group) => (
            <div
              key={group.label}
              className={markdownStyles.toolbarGroup}
              role="group"
              aria-label={group.label}
            >
              {group.actions.map(({ format, icon, label, showLabel }) => (
                <button
                  key={format}
                  type="button"
                  className={`${markdownStyles.toolbarButton} ${
                    showLabel ? markdownStyles.labeledToolbarButton : ''
                  }`}
                  aria-label={label}
                  title={label}
                  disabled={disabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => formatSelection(format)}
                >
                  {icon}
                  {showLabel && <span>{label}</span>}
                </button>
              ))}
            </div>
          ))}

          <div
            className={markdownStyles.toolbarGroup}
            role="group"
            aria-label="History"
          >
            <button
              type="button"
              className={markdownStyles.toolbarButton}
              aria-label="Undo"
              title="Undo"
              disabled={disabled || !canUndo}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => moveThroughHistory(-1)}
            >
              <UndoIcon />
            </button>
            <button
              type="button"
              className={markdownStyles.toolbarButton}
              aria-label="Redo"
              title="Redo"
              disabled={disabled || !canRedo}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => moveThroughHistory(1)}
            >
              <RedoIcon />
            </button>
          </div>
        </div>

        <div className={markdownStyles.surface}>
          <textarea
            ref={textareaRef}
            id={id}
            className={markdownStyles.textarea}
            value={value}
            onChange={(event) => updateValue(event.currentTarget.value)}
            maxLength={effectiveMaxLength}
            disabled={disabled}
            placeholder={placeholder}
            aria-describedby={describedBy}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            onKeyDown={handleTextareaKeyDown}
          />
        </div>
      </div>

      <div
        id={previewPanelId}
        className={markdownStyles.previewPanel}
        role="tabpanel"
        aria-labelledby={previewTabId}
        tabIndex={mode === 'preview' ? 0 : -1}
        hidden={mode !== 'preview'}
      >
        {mode === 'preview' &&
          (value.trim() ? (
            <MarkdownRenderer markdown={value} maxLength={effectiveMaxLength} />
          ) : (
            <p className={markdownStyles.emptyPreview}>{emptyPreviewMessage}</p>
          ))}
      </div>

      <div id={statusId} className={markdownStyles.status}>
        {safetyError ? (
          <span className={markdownStyles.invalidStatus} role="alert">
            <span
              className={markdownStyles.invalidStatusIcon}
              aria-hidden="true"
            >
              !
            </span>
            {safetyError}
          </span>
        ) : (
          <span className={markdownStyles.supportedStatus}>
            <StatusCheckIcon className={markdownStyles.statusCheckIcon} />
            Markdown supported
          </span>
        )}
        <span className={markdownStyles.characterCount}>
          {value.length.toLocaleString()} /{' '}
          {effectiveMaxLength.toLocaleString()} characters
        </span>
      </div>
    </div>
  );
};

export { MARKDOWN_MAX_LENGTH } from './markdownSafety';
